import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, 
  Clock, 
  Users, 
  Phone, 
  Plus, 
  CheckCircle, 
  AlertCircle, 
  User, 
  MapPin, 
  Search,
  Send,
  Edit3,
  Trash2,
  UserCheck,
  UserX,
  RefreshCw,
  X
} from 'lucide-react';
import axios from 'axios';

const MainDashboard = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employeeSchedules, setEmployeeSchedules] = useState([]);
  const [urgentCareAppointments, setUrgentCareAppointments] = useState([]);
  const [todaysReminders, setTodaysReminders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [appointmentReminder, setAppointmentReminder] = useState({
    date: '',
    time: '',
    message: ''
  });
  const [newReminder, setNewReminder] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [statusChange, setStatusChange] = useState({ appointmentId: null, newStatus: '', currentStatus: '' });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");
  const themeColor = 'var(--theme-primary)';

  // Debug log to check customers state
  useEffect(() => {
    console.log('Customers state updated:', customers, 'Type:', typeof customers, 'Is Array:', Array.isArray(customers));
  }, [customers]);

  // SMS Templates - loaded from API
  const [reminderTemplates, setReminderTemplates] = useState([]);

  useEffect(() => {
    if (token) {
      fetchDashboardData();
      loadTodaysReminders();
    }
  }, [token]);

  // Auto-refresh urgent care appointments every 30 seconds
  useEffect(() => {
    if (!token) return;

    const appointmentRefreshInterval = setInterval(() => {
      console.log('Auto-refreshing urgent care appointments...');
      fetchUrgentCareAppointments();
    }, 30000); // 30 seconds

    // Cleanup interval on component unmount
    return () => {
      clearInterval(appointmentRefreshInterval);
    };
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchEmployeeSchedules(),
        fetchUrgentCareAppointments(),
        fetchCustomers(),
        fetchSMSTemplates()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setMessage({ type: 'error', text: 'Failed to load dashboard data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSMSTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates?template_type=sms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const templatesData = await response.json();
        console.log('Fetched SMS templates:', templatesData);
        
        // Filter only enabled templates for Main Dashboard
        const enabledTemplates = templatesData.filter(template => template.is_enabled !== false);
        console.log('Enabled SMS templates for Main Dashboard:', enabledTemplates);
        
        // Map to the expected format
        const formattedTemplates = enabledTemplates.map(template => ({
          id: template.id,
          name: template.name,
          template: template.content // The existing API uses 'content' instead of 'template'
        }));
        
        setReminderTemplates(formattedTemplates);
      } else {
        console.warn('Failed to fetch SMS templates, using fallback templates');
        // Fallback to default templates if API fails
        setReminderTemplates([
          {
            id: 'default-1',
            name: 'General Checkup',
            template: `Hi [CUSTOMER_NAME]! This is a reminder that [PET_NAME] has an appointment scheduled for [DATE] at [TIME] for a general checkup. Please arrive 10 minutes early. Reply STOP to opt out.`
          },
          {
            id: 'default-2', 
            name: 'Vaccination',
            template: `Hello [CUSTOMER_NAME], [PET_NAME]'s vaccination appointment is scheduled for [DATE] at [TIME]. Please bring vaccination records if available. Reply STOP to opt out.`
          },
          {
            id: 'default-3',
            name: 'Surgery Follow-up',
            template: `Hi [CUSTOMER_NAME], this is a reminder for [PET_NAME]'s follow-up appointment on [DATE] at [TIME]. Please ensure your pet is fasted if required. Reply STOP to opt out.`
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching SMS templates:', error);
      // Use fallback templates on error
      setReminderTemplates([
        {
          id: 'default-1',
          name: 'General Checkup',
          template: `Hi [CUSTOMER_NAME]! This is a reminder that [PET_NAME] has an appointment scheduled for [DATE] at [TIME] for a general checkup. Please arrive 10 minutes early. Reply STOP to opt out.`
        }
      ]);
    }
  };

  const fetchEmployeeSchedules = async () => {
    try {
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      
      // First, get all scheduled shifts for today
      const shiftsResponse = await fetch(`${API_BASE_URL}/api/schedule/shifts?start_date=${today}&end_date=${today}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Shifts API response status:', shiftsResponse.status);

      if (!shiftsResponse.ok) {
        console.error('Failed to fetch shifts:', shiftsResponse.status);
        setEmployeeSchedules([]);
        return;
      }

      const shifts = await shiftsResponse.json();
      console.log('Fetched shifts for today:', shifts);

      if (!Array.isArray(shifts) || shifts.length === 0) {
        console.log('No shifts scheduled for today');
        setEmployeeSchedules([]);
        return;
      }

      // For each scheduled employee, get their clock-in status
      const employeesWithStatus = await Promise.all(
        shifts.map(async (shift) => {
          try {
            // Get user details first
            const userResponse = await fetch(`${API_BASE_URL}/api/employees/${shift.user_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            let userName = 'Unknown Employee';
            let userRole = 'Unknown';

            if (userResponse.ok) {
              const userData = await userResponse.json();
              userName = userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.name || 'Unknown Employee';
              userRole = userData.role || userData.position || 'Employee';
            } else {
              // Try alternative endpoint
              const altUserResponse = await fetch(`${API_BASE_URL}/api/users/${shift.user_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              if (altUserResponse.ok) {
                const userData = await altUserResponse.json();
                userName = userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.name || 'Unknown Employee';
                userRole = userData.role || userData.position || 'Employee';
              }
            }

            // Try to get current timesheet status for this user
            let clockStatus = 'not_clocked_in';
            let clockInTime = null;

            try {
              // Check if user has an active time entry
              const entriesResponse = await fetch(`${API_BASE_URL}/api/timesheet/all-entries?user_id=${shift.user_id}&start_date=${today}&end_date=${today}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (entriesResponse.ok) {
                const entries = await entriesResponse.json();
                if (Array.isArray(entries) && entries.length > 0) {
                  // Find active entry or most recent entry for today
                  const activeEntry = entries.find(entry => entry.status === 'active');
                  const todaysEntry = activeEntry || entries.find(entry => {
                    const entryDate = new Date(entry.clock_in_time).toISOString().split('T')[0];
                    return entryDate === today;
                  });

                  if (todaysEntry) {
                    if (todaysEntry.status === 'active' || !todaysEntry.clock_out_time) {
                      clockStatus = 'clocked_in';
                      clockInTime = todaysEntry.clock_in_time;
                    } else {
                      clockStatus = 'clocked_out';
                      clockInTime = todaysEntry.clock_in_time;
                    }
                  }
                }
              }
            } catch (statusError) {
              console.warn('Failed to get clock status for user:', shift.user_id, statusError);
            }

            return {
              user_id: shift.user_id,
              name: userName,
              role: userRole,
              start_time: shift.start_time,
              end_time: shift.end_time,
              clockStatus: clockStatus,
              clockInTime: clockInTime,
              shift_id: shift.id
            };
          } catch (error) {
            console.error('Error processing shift for user:', shift.user_id, error);
            return {
              user_id: shift.user_id,
              name: 'Unknown Employee',
              role: 'Employee',
              start_time: shift.start_time,
              end_time: shift.end_time,
              clockStatus: 'not_clocked_in',
              clockInTime: null,
              shift_id: shift.id
            };
          }
        })
      );
      
      console.log('Employees with status:', employeesWithStatus);
      setEmployeeSchedules(employeesWithStatus);
      
    } catch (error) {
      console.error('Error fetching employee schedules:', error);
      // Only show empty state, don't fall back to sample data
      setEmployeeSchedules([]);
    }
  };

  // Get status color function (matches urgent care appointments page)
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#059669';
      case 'cancelled': return '#dc2626';
      case 'no_show': return '#d97706';
      case 'abandoned': return '#6b7280';
      case 'verified': return '#7c3aed';
      case 'checked_in': return '#059669';
      default: return '#2563eb';
    }
  };

  // Handle status change with confirmation
  const handleStatusChange = (appointmentId, newStatus, currentStatus) => {
    setStatusChange({ appointmentId, newStatus, currentStatus });
    setShowStatusConfirm(true);
  };

  // Confirm status change
  const confirmStatusChange = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/urgent-care-appointments/${statusChange.appointmentId}/status?status=${statusChange.newStatus}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let message = `Appointment status updated to ${statusChange.newStatus}`;
        if (data.freed_slot) {
          message += ` Time slot ${data.freed_slot} is now available for booking.`;
        }
        setMessage({ type: 'success', text: message });
        
        // Refresh the appointments list to show updated status
        await fetchUrgentCareAppointments();
        setShowStatusConfirm(false);
        setStatusChange({ appointmentId: null, newStatus: '', currentStatus: '' });
        
        // Clear message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to update appointment status' });
      }
    } catch (error) {
      console.error('Error updating appointment status:', error);
      setMessage({ type: 'error', text: 'Unable to update appointment status. Please try again.' });
    }
  };

  const fetchUrgentCareAppointments = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        console.log('Manual refresh of urgent care appointments triggered');
        setShowRefreshModal(true); // Show refresh modal
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Use the same API endpoint as UrgentCareAppointments page
      const response = await fetch(`${API_BASE_URL}/api/urgent-care-appointments?filter_days=today&limit=10`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Urgent Care Appointments API response status:', response.status);

      if (response.ok) {
        const appointmentsData = await response.json();
        console.log('Raw urgent care appointments data:', appointmentsData);
        
        let appointments = [];
        if (appointmentsData.appointments && Array.isArray(appointmentsData.appointments)) {
          appointments = appointmentsData.appointments;
        } else if (Array.isArray(appointmentsData)) {
          appointments = appointmentsData;
        }

        // Get current time for filtering upcoming appointments only
        const now = new Date();
        
        // Filter for upcoming appointments only and sort by time
        const upcomingAppointments = appointments
          .filter(appointment => {
            if (!appointment.appointment_time) return false;
            
            try {
              // Since appointments are now stored in business timezone, parse directly
              const appointmentDateTime = new Date(appointment.appointment_time);
              const isUpcoming = appointmentDateTime > now; // Only future appointments
              
              console.log(`MainDashboard appointment filter: ${appointment.appointment_time} -> ${appointmentDateTime.toISOString()} vs now ${now.toISOString()} = ${isUpcoming ? 'upcoming' : 'past'}`);
              
              return isUpcoming;
            } catch (error) {
              console.warn('Error parsing appointment time for filtering:', appointment.appointment_time);
              return false;
            }
          })
          .sort((a, b) => {
            // Sort by appointment time (earliest first)
            // Since appointments are stored in business timezone, parse directly
            const timeA = new Date(a.appointment_time);
            const timeB = new Date(b.appointment_time);
            return timeA - timeB;
          })
          .slice(0, 3); // Get next 3 upcoming appointments

        // Process appointments with proper timezone handling (convert UTC to Eastern Time)
        const processedAppointments = upcomingAppointments.map(appointment => {
          let displayTime = 'Time not available';
          
          if (appointment.appointment_time) {
            try {
              // Since appointments are now stored in business timezone, parse directly
              const date = new Date(appointment.appointment_time);
              
              if (!isNaN(date.getTime())) {
                displayTime = date.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
                
                console.log(`MainDashboard time formatting: ${appointment.appointment_time} -> ${displayTime} (Business timezone)`);
              } else {
                console.warn('Invalid appointment time:', appointment.appointment_time);
                displayTime = 'Invalid time';
              }
            } catch (error) {
              console.error('Error formatting appointment time:', appointment.appointment_time, error);
              displayTime = 'Time error';
            }
          }

          return {
            id: appointment.id,
            customer_name: appointment.owner_first_name && appointment.owner_last_name 
              ? `${appointment.owner_first_name} ${appointment.owner_last_name}`
              : appointment.customer_name || 'Unknown Customer',
            pet_name: appointment.pet_name || 'Unknown Pet',
            appointment_time: displayTime,
            reason: appointment.reason_for_visit || appointment.description || appointment.reason || appointment.visit_reason || 'General visit',
            status: appointment.status || 'confirmed',
            raw_time: appointment.appointment_time // Keep for debugging
          };
        });

        console.log('Processed upcoming urgent care appointments:', processedAppointments);
        setUrgentCareAppointments(processedAppointments);
        
        if (isManualRefresh) {
          // Hide refresh modal after successful refresh
          setTimeout(() => {
            setShowRefreshModal(false);
          }, 500); // Small delay to show the modal briefly
        }
        
      } else {
        console.warn('Urgent Care Appointments API failed, status:', response.status);
        const responseText = await response.text();
        console.warn('API Error response:', responseText);
        setUrgentCareAppointments([]);
        
        if (isManualRefresh) {
          setShowRefreshModal(false); // Hide modal on error
        }
      }
      
    } catch (error) {
      console.error('Error fetching urgent care appointments:', error);
      setUrgentCareAppointments([]);
      
      if (isManualRefresh) {
        setShowRefreshModal(false); // Hide modal on error
      }
    }
  };

  const handleManualRefresh = () => {
    fetchUrgentCareAppointments(true);
  };

  const fetchCustomers = async () => {
    try {
      // Use the same API endpoint as the Customer Management page
      const response = await fetch(`${API_BASE_URL}/api/customers?limit=100`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Customers API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Customers API response data:', data);
        
        // Use the same structure as Customer Management page
        if (data.customers && Array.isArray(data.customers)) {
          setCustomers(data.customers);
          console.log('Successfully loaded', data.customers.length, 'customers');
        } else {
          console.warn('Customers API returned unexpected format:', data);
          setCustomers([]);
        }
      } else {
        console.error('Customers API failed with status:', response.status);
        // Fallback to sample data only if API fails
        setCustomers([
          {
            id: '1',
            first_name: 'John',
            last_name: 'Smith',
            email: 'john@example.com',
            phone: '(555) 123-4567',
            pets: [{ name: 'Buddy' }, { name: 'Whiskers' }]
          },
          {
            id: '2',
            first_name: 'Emma',
            last_name: 'Davis', 
            email: 'emma@example.com',
            phone: '(555) 987-6543',
            pets: [{ name: 'Luna' }]
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      // Fallback to sample data only if there's a network error
      setCustomers([
        {
          id: '1',
          first_name: 'John',
          last_name: 'Smith',
          email: 'john@example.com',
          phone: '(555) 123-4567',
          pets: [{ name: 'Buddy' }, { name: 'Whiskers' }]
        },
        {
          id: '2',
          first_name: 'Emma',
          last_name: 'Davis', 
          email: 'emma@example.com',
          phone: '(555) 987-6543',
          pets: [{ name: 'Luna' }]
        }
      ]);
    }
  };

  const loadTodaysReminders = () => {
    const stored = localStorage.getItem('todaysReminders');
    if (stored) {
      setTodaysReminders(JSON.parse(stored));
    }
  };

  const saveTodaysReminders = (reminders) => {
    localStorage.setItem('todaysReminders', JSON.stringify(reminders));
    setTodaysReminders(reminders);
  };

  const addReminder = () => {
    if (!newReminder.trim()) return;
    
    const reminder = {
      id: Date.now(),
      text: newReminder,
      completed: false,
      createdAt: new Date().toISOString()
    };
    
    const updatedReminders = [...todaysReminders, reminder];
    saveTodaysReminders(updatedReminders);
    setNewReminder('');
  };

  const toggleReminder = (id) => {
    const updatedReminders = todaysReminders.map(reminder =>
      reminder.id === id ? { ...reminder, completed: !reminder.completed } : reminder
    );
    saveTodaysReminders(updatedReminders);
  };

  const deleteReminder = (id) => {
    const updatedReminders = todaysReminders.filter(reminder => reminder.id !== id);
    saveTodaysReminders(updatedReminders);
  };

  const handleCustomerSearch = (searchTerm) => {
    setCustomerSearch(searchTerm);
    // Ensure customers is always an array before filtering
    if (searchTerm.length > 1 && Array.isArray(customers)) {
      const filtered = customers.filter(customer => {
        if (!customer) return false;
        
        // Handle both name formats: single 'name' field or 'first_name' + 'last_name'
        const fullName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
        const firstName = customer.first_name || '';
        const lastName = customer.last_name || '';
        
        return fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               lastName.toLowerCase().includes(searchTerm.toLowerCase());
      });
      console.log(`Found ${filtered.length} customers matching "${searchTerm}"`);
    }
  };

  const updateTemplateWithDateTime = (date, time) => {
    if (selectedCustomer && appointmentReminder.message) {
      const customerName = selectedCustomer.name || `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim();
      
      // Format date in a friendly way
      let formattedDate = '[DATE]';
      if (date) {
        const dateObj = new Date(date);
        formattedDate = dateObj.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
      
      // Format time in a friendly way
      let formattedTime = '[TIME]';
      if (time) {
        const [hours, minutes] = time.split(':');
        const hour12 = parseInt(hours) % 12 || 12;
        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
        formattedTime = `${hour12}:${minutes} ${ampm}`;
      }
      
      // Update the current message with new date/time
      let updatedMessage = appointmentReminder.message
        .replace(/\[DATE\]|(\w+day,\s+\w+\s+\d{1,2},\s+\d{4})/g, formattedDate)
        .replace(/\[TIME\]|(\d{1,2}:\d{2}\s+(AM|PM))/g, formattedTime);
      
      setAppointmentReminder(prev => ({ 
        ...prev, 
        message: updatedMessage 
      }));
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    
    // Handle both name formats
    const customerName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    setCustomerSearch(customerName);
    
    // Auto-populate template with customer info
    if (customer.pets && customer.pets.length > 0) {
      const template = reminderTemplates[0].template
        .replace('[CUSTOMER_NAME]', customerName)
        .replace('[PET_NAME]', customer.pets[0].name || '[PET_NAME]')
        .replace('[DATE]', appointmentReminder.date || '[DATE]')
        .replace('[TIME]', appointmentReminder.time || '[TIME]');
      
      setAppointmentReminder(prev => ({ ...prev, message: template }));
    }
  };

  const sendAppointmentReminder = async () => {
    if (!selectedCustomer || !appointmentReminder.message) {
      setMessage({ type: 'error', text: 'Please select a customer and enter a message' });
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/sms/send`, {
        to: selectedCustomer.phone,
        message: appointmentReminder.message
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Appointment reminder sent successfully!' });
      
      // Reset form
      setSelectedCustomer(null);
      setCustomerSearch('');
      setAppointmentReminder({ date: '', time: '', message: '' });
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      setMessage({ type: 'error', text: 'Failed to send appointment reminder' });
    }

    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`2024-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getClockStatusBadge = (status) => {
    switch (status) {
      case 'clocked_in':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <UserCheck className="h-3 w-3 mr-1" />
            Clocked In
          </span>
        );
      case 'clocked_out':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            <UserX className="h-3 w-3 mr-1" />
            Clocked Out
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Not Clocked In
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: themeColor }}></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 rounded-md p-4 ${
            message.type === 'error' 
              ? 'bg-red-50 border border-red-200' 
              : 'bg-green-50 border border-green-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {message.type === 'error' ? (
                  <AlertCircle className="h-5 w-5 text-red-400" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  message.type === 'error' ? 'text-red-800' : 'text-green-800'
                }`}>
                  {message.text}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* SMS Appointment Reminders Widget - Top left */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <Phone className="h-5 w-5 mr-2" style={{ color: themeColor }} />
                <h2 className="text-lg font-semibold text-gray-900">Send Customer SMS</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Customer Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Customer
                </label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => handleCustomerSearch(e.target.value)}
                    placeholder="Start typing customer name..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  
                  {/* Search Results Dropdown */}
                  {customerSearch.length > 1 && !selectedCustomer && Array.isArray(customers) && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {customers
                        .filter(customer => {
                          if (!customer) return false;
                          
                          // Handle both name formats: single 'name' field or 'first_name' + 'last_name'
                          const fullName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                          const firstName = customer.first_name || '';
                          const lastName = customer.last_name || '';
                          
                          return fullName.toLowerCase().includes(customerSearch.toLowerCase()) ||
                                 firstName.toLowerCase().includes(customerSearch.toLowerCase()) ||
                                 lastName.toLowerCase().includes(customerSearch.toLowerCase());
                        })
                        .map((customer) => {
                          const customerName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                          return (
                            <button
                              key={customer.id}
                              onClick={() => selectCustomer(customer)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{customerName}</div>
                              <div className="text-sm text-gray-500">{customer.phone}</div>
                              <div className="text-xs text-gray-400">
                                Pets: {customer.pets?.map(p => p.name).join(', ') || 'No pets listed'}
                              </div>
                            </button>
                          );
                        })}
                      
                      {customers.filter(customer => {
                        if (!customer) return false;
                        
                        const fullName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                        const firstName = customer.first_name || '';
                        const lastName = customer.last_name || '';
                        
                        return fullName.toLowerCase().includes(customerSearch.toLowerCase()) ||
                               firstName.toLowerCase().includes(customerSearch.toLowerCase()) ||
                               lastName.toLowerCase().includes(customerSearch.toLowerCase());
                      }).length === 0 && (
                        <div className="px-4 py-2 text-gray-500 text-center">
                          No customers found matching "{customerSearch}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Customer Info */}
              {selectedCustomer && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {selectedCustomer.name || `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim()}
                      </h4>
                      <p className="text-sm text-gray-600">{selectedCustomer.phone}</p>
                      <p className="text-xs text-gray-500">
                        Pets: {selectedCustomer.pets?.map(p => p.name).join(', ') || 'No pets listed'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerSearch('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Appointment Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Date
                  </label>
                  <input
                    type="date"
                    value={appointmentReminder.date}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setAppointmentReminder(prev => ({ ...prev, date: newDate }));
                      updateTemplateWithDateTime(newDate, appointmentReminder.time);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Time
                  </label>
                  <input
                    type="time"
                    value={appointmentReminder.time}
                    onChange={(e) => {
                      const newTime = e.target.value;
                      setAppointmentReminder(prev => ({ ...prev, time: newTime }));
                      updateTemplateWithDateTime(appointmentReminder.date, newTime);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Message Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder Message
                </label>
                <textarea
                  value={appointmentReminder.message}
                  onChange={(e) => setAppointmentReminder(prev => ({ ...prev, message: e.target.value }))}
                  rows="4"
                  placeholder="Enter your appointment reminder message..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Template Buttons */}
              <div className="flex flex-wrap gap-2">
                {reminderTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => {
                      let message = template.template;
                      if (selectedCustomer) {
                        const customerName = selectedCustomer.name || `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim();
                        message = message
                          .replace('[CUSTOMER_NAME]', customerName)
                          .replace('[PET_NAME]', selectedCustomer.pets?.[0]?.name || '[PET_NAME]');
                      }
                      
                      // Use current date and time values
                      let formattedDate = '[DATE]';
                      if (appointmentReminder.date) {
                        const dateObj = new Date(appointmentReminder.date);
                        formattedDate = dateObj.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        });
                      }
                      
                      let formattedTime = '[TIME]';
                      if (appointmentReminder.time) {
                        const [hours, minutes] = appointmentReminder.time.split(':');
                        const hour12 = parseInt(hours) % 12 || 12;
                        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                        formattedTime = `${hour12}:${minutes} ${ampm}`;
                      }
                      
                      message = message
                        .replace('[DATE]', formattedDate)
                        .replace('[TIME]', formattedTime);
                      
                      setAppointmentReminder(prev => ({ ...prev, message }));
                    }}
                    className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {template.name}
                  </button>
                ))}
              </div>

              {/* Send Button */}
              <button
                onClick={sendAppointmentReminder}
                disabled={!selectedCustomer || !appointmentReminder.message}
                className="w-full flex items-center justify-center px-4 py-2 text-white font-medium rounded-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: themeColor }}
              >
                <Send className="h-4 w-4 mr-2" />
                Send Appointment Reminder
              </button>
            </div>
          </div>

          {/* Urgent Care Appointments Widget - Moved to top right */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Upcoming Today's Appointments</h2>
                </div>
                <button
                  onClick={handleManualRefresh}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title="Refresh appointments"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {urgentCareAppointments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No urgent care appointments today</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {urgentCareAppointments.map((appointment) => (
                    <div key={appointment.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{appointment.customer_name}</h3>
                        <p className="text-sm text-gray-600">Pet: {appointment.pet_name}</p>
                        <p className="text-sm text-gray-500">{appointment.reason}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium mb-2" style={{ color: themeColor }}>
                          {appointment.appointment_time}
                        </p>
                        <select
                          value={appointment.status || 'scheduled'}
                          onChange={(e) => handleStatusChange(appointment.id, e.target.value, appointment.status || 'scheduled')}
                          className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-w-[100px]"
                          style={{ color: getStatusColor(appointment.status || 'scheduled') }}
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="verified">Verified</option>
                          <option value="checked_in">Checked In</option>
                          <option value="completed">Completed</option>
                          <option value="no_show">No Show</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="abandoned">Abandoned</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Employee Schedule Widget - Moved to bottom left */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2" style={{ color: themeColor }} />
                <h2 className="text-lg font-semibold text-gray-900">Employee Schedule</h2>
              </div>
            </div>
            <div className="p-6">
              {employeeSchedules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No employees scheduled for today</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {employeeSchedules.map((employee) => (
                    <div key={employee.user_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{employee.name}</h3>
                        <p className="text-sm text-gray-600">{capitalizeFirstLetter(employee.role)}</p>
                        <p className="text-sm text-gray-500">
                          {formatTime(employee.start_time)} - {formatTime(employee.end_time)}
                        </p>
                      </div>
                      <div>
                        {getClockStatusBadge(employee.clockStatus)}
                        {employee.clockInTime && (
                          <p className="text-xs text-gray-500 mt-1">
                            In: {formatTime(new Date(employee.clockInTime).toTimeString().slice(0, 5))}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Reminders Widget */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" style={{ color: themeColor }} />
                  <h2 className="text-lg font-semibold text-gray-900">Reminders</h2>
                </div>
                <span className="text-sm text-gray-500">
                  {todaysReminders.filter(r => !r.completed).length} pending
                </span>
              </div>
            </div>
            <div className="p-6">
              {/* Add new reminder */}
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="text"
                  value={newReminder}
                  onChange={(e) => setNewReminder(e.target.value)}
                  placeholder="Add a new reminder..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && addReminder()}
                />
                <button
                  onClick={addReminder}
                  className="px-3 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: themeColor }}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {/* Reminders list */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {todaysReminders.map((reminder) => (
                  <div key={reminder.id} className={`flex items-center space-x-3 p-3 rounded-lg ${
                    reminder.completed ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <button
                      onClick={() => toggleReminder(reminder.id)}
                      className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        reminder.completed
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {reminder.completed && <CheckCircle className="h-3 w-3" />}
                    </button>
                    <span className={`flex-1 ${
                      reminder.completed ? 'line-through text-gray-500' : 'text-gray-900'
                    }`}>
                      {reminder.text}
                    </span>
                    <button
                      onClick={() => deleteReminder(reminder.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                
                {todaysReminders.length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <p>No reminders for today</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Status Change Confirmation Modal */}
      {showStatusConfirm && statusChange.appointmentId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 rounded-t-xl" style={{ backgroundColor: themeColor }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Confirm Status Change</h3>
                <button
                  onClick={() => setShowStatusConfirm(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-8 w-8 text-orange-500 mr-3" />
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Update Appointment Status</h4>
                  <p className="text-sm text-gray-500">Confirm the status change for this appointment</p>
                </div>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-orange-800">
                  Are you sure you want to change the status from <strong className="capitalize">{statusChange.currentStatus}</strong> to <strong className="capitalize">{statusChange.newStatus}</strong>?
                </p>
                {statusChange.newStatus === 'abandoned' && (
                  <p className="text-sm text-orange-700 mt-2">
                    <strong>Note:</strong> Setting status to "Abandoned" will free up the appointment time slot for new bookings.
                  </p>
                )}
              </div>
            </div>
              
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-100 border-t border-gray-200 flex justify-end gap-3 rounded-b-xl">
              <button
                onClick={() => setShowStatusConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-colors"
                style={{ backgroundColor: themeColor }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = themeColor}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Modal */}
      {showRefreshModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center">
            <div className="flex items-center justify-center mb-4">
              <RefreshCw className="h-8 w-8 animate-spin" style={{ color: themeColor }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Refreshing...</h3>
            <p className="text-gray-600">Updating urgent care appointments</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainDashboard;